import { useRouter } from 'next/compat/router';
import { useEffect } from 'react';

import { useBreadcrumbStore } from 'stores/use-breadcrumb-store';

type UseBreadcrumbsProps = {
	baseName?: string;
};

export const useBreadcrumbs = () => {
	const router = useRouter();
	const { breadcrumbs, resetBreadcrumbs } = useBreadcrumbStore((state) => ({
		breadcrumbs: state.breadcrumbs,
		resetBreadcrumbs: state.resetBreadcrumbs,
	}));

	useEffect(() => {
		if (!router?.isReady) return;

		resetBreadcrumbs();

		const handleRouteStart = () => resetBreadcrumbs();
		router.events?.on?.('routeChangeStart', handleRouteStart);

		return () => {
			router.events?.off?.('routeChangeStart', handleRouteStart);
		};
	}, [router?.events, resetBreadcrumbs, router?.isReady]);

	function generateNextBreadcrumbs({ baseName }: UseBreadcrumbsProps) {
		const asPathWithoutQuery = router?.asPath?.split('?')[0].split('#')[0];
		const asPathNestedRoutes = asPathWithoutQuery
			?.split('/')
			?.filter((router) => router?.length > 0);

		const crumbList =
			asPathNestedRoutes?.map((subpath, id) => {
				const pathname =
					'/' + asPathNestedRoutes?.slice(0, id + 1)?.join('/') || '';
				const href = `${pathname}` || '/';
				const title = customTitle(decodeURIComponent(subpath), href);

				return { href, title, id: id + 1 };
			}) || [];

		return [{ href: '/', title: baseName || 'Home', id: 0 }, ...crumbList];
	}

	function generateCustomBreadcrumbs({ baseName }: UseBreadcrumbsProps) {
		if (baseName) {
			return [
				{ href: '/', title: baseName || baseName, id: 0 },
				...breadcrumbs,
			];
		}

		return [
			{ href: '/', title: baseName || 'Home', id: 0 },
			...breadcrumbs,
		];
	}

	function customTitle(title: string, href: string) {
		if (href.includes('/subscriptions/')) {
			return 'subscription update';
		}

		return title.replaceAll('-', ' ');
	}

	return { generateNextBreadcrumbs, generateCustomBreadcrumbs };
};
